<template>
  <el-dialog :title="isEdit ? '编辑工具' : '新增工具'" :visible="visible"
             width="680px" v-dialogDrag :modal-append-to-body="false" custom-class="tool_edit_dialog"
             @update:visible="$emit('update:visible', $event)" @close="onClose">
    <el-form ref="form" :model="form" :rules="rules" label-width="110px">
      <el-form-item label="名称" prop="name">
        <el-input v-model="form.name" maxlength="40" placeholder="小写字母/数字/下划线/中划线，2-40 字符" />
      </el-form-item>
      <el-form-item label="说明">
        <el-input v-model="form.description" type="textarea" :rows="2" maxlength="200" />
      </el-form-item>
      <el-form-item label="类型">
        <el-radio-group v-model="form.type" :disabled="isEdit" @change="onTypeChange">
          <el-radio label="http">HTTP 工具</el-radio>
          <el-radio label="mcp">MCP 工具</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="启用">
        <el-switch v-model="form.enabled" active-text="启用" />
      </el-form-item>

      <template v-if="form.type === 'http'">
        <el-form-item label="请求方法">
          <el-select v-model="form.http_method" style="width: 140px">
            <el-option v-for="m in ['GET', 'POST', 'PUT', 'DELETE', 'PATCH']" :key="m" :label="m" :value="m" />
          </el-select>
        </el-form-item>
        <el-form-item label="请求地址" prop="http_url">
          <!-- URL 仅渲染 ${secret.X}；{{arg.*}} 只在请求体模板生效 -->
          <el-input v-model="form.http_url" :placeholder="'https://api.example.com/v1/echo'" />
        </el-form-item>
        <el-form-item label="附加请求头">
          <div class="kv_rows">
            <div v-for="(row, i) in headerRows" :key="'h' + i" class="kv_row">
              <el-input v-model="row.k" placeholder="Header 名" size="small" />
              <el-input v-model="row.v" placeholder="值，支持 ${secret.X}" size="small" />
              <el-button type="text" size="small" class="danger_text" @click="headerRows.splice(i, 1)">删除</el-button>
            </div>
            <el-button type="text" size="small" icon="el-icon-plus" @click="headerRows.push({ k: '', v: '' })">添加请求头</el-button>
          </div>
        </el-form-item>
        <el-form-item label="认证方式">
          <el-radio-group v-model="form.http_auth_type">
            <el-radio label="none">无</el-radio>
            <el-radio label="bearer">Bearer</el-radio>
            <el-radio label="basic">Basic</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="form.http_auth_type !== 'none'" label="认证数据" prop="http_auth_data">
          <el-input v-model="form.http_auth_data" :placeholder="form.http_auth_type === 'bearer' ? 'Token，支持 ${secret.X}' : 'user:pass，支持 ${secret.X}'" />
        </el-form-item>
        <el-form-item label="请求体模板">
          <el-input v-model="form.body_template" type="textarea" :rows="4" class="mono" :placeholder="'JSON 模板，如 {&quot;q&quot;: {{arg.q}}}'" />
          <div class="field_tip" v-pre>{{arg.x}} 替换为 JSON 编码后的入参值：字符串自动带引号与转义（占位符本身不要加引号），数字/布尔原样；GET/HEAD 不支持请求体</div>
          <div v-if="jsonErr.body_template" class="json_err">{{ jsonErr.body_template }}</div>
        </el-form-item>
      </template>

      <template v-else>
        <el-form-item label="MCP 服务" prop="mcp_server_id">
          <el-select v-model="form.mcp_server_id" filterable placeholder="选择 MCP 服务器" style="width: 100%"
                     @change="onMcpServerChange">
            <el-option v-for="s in serverOptions" :key="s.server_id" :label="s.name" :value="s.server_id" />
          </el-select>
        </el-form-item>
        <el-form-item label="远端工具" prop="mcp_tool_name">
          <div class="mcp_tool_bar">
            <el-select v-model="form.mcp_tool_name" filterable placeholder="拉取列表后选择" style="flex: 1"
                       @change="applyMcpToolSchema">
              <el-option v-for="t in mcpToolOptions" :key="t.name" :label="t.name" :value="t.name">
                <span class="mcp_opt_name">{{ t.name }}</span>
                <span class="mcp_opt_desc">{{ t.description || '' }}</span>
              </el-option>
            </el-select>
            <el-button size="small" :loading="fetchingTools" @click="fetchMcpTools">拉取工具列表</el-button>
            <el-button v-if="isEdit" size="small" type="primary" plain :loading="fetchingTools || submiting"
                       @click="refreshAndSaveMcpTool">重新拉取并更新</el-button>
          </div>
        </el-form-item>
      </template>

      <el-form-item label="参数 Schema">
        <el-input v-model="form.args_schema" type="textarea" :rows="4" class="mono"
                  :disabled="form.type === 'mcp'" :placeholder="form.type === 'mcp' ? '选择远端工具后自动拉取' : '参数 JSON Schema（可选）'" />
        <div v-if="form.type === 'mcp'" class="field_tip">由远端 MCP 工具的 inputSchema 自动填充，保存后生效</div>
        <div v-if="jsonErr.args_schema" class="json_err">{{ jsonErr.args_schema }}</div>
      </el-form-item>
      <el-form-item label="超时(秒)">
        <el-input-number v-model="form.timeout_sec" :min="0" :max="120" />
        <span class="field_tip">0-120，缺省 30</span>
      </el-form-item>
      <el-form-item v-if="scope === 'platform'" label="计费倍数">
        <el-input-number v-model="form.bill_amount" :min="0" />
        <span class="field_tip">仅平台公共工具生效，运行时 &lt;1 按 1 计</span>
      </el-form-item>
      <el-form-item label="密钥 Secrets">
        <div class="kv_rows">
          <div class="kv_tip">AES 加密存储；headers/认证数据中用 ${secret.X} 引用；编辑时值显示 *** 表示保留原密文</div>
          <div v-for="(row, i) in secretRows" :key="'s' + i" class="kv_row">
            <el-input v-model="row.k" placeholder="Key" size="small" />
            <el-input v-model="row.v" placeholder="值（编辑回填 *** 即保留）" size="small" />
            <el-button type="text" size="small" class="danger_text" @click="secretRows.splice(i, 1)">删除</el-button>
          </div>
          <el-button type="text" size="small" icon="el-icon-plus" @click="secretRows.push({ k: '', v: '' })">添加密钥</el-button>
        </div>
      </el-form-item>
    </el-form>
    <div slot="footer">
      <el-button @click="$emit('update:visible', false)">取 消</el-button>
      <el-button type="primary" :loading="submiting" @click="submitForm">确 定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { agentBody, mapToRows, rowsToMap } from '../utils'

// 工具新增/编辑弹窗：http/mcp 双模式；编辑先 get 全量回填（secrets 掩码 *** 原样提交即保留原密文）
export default {
  name: 'AiAgentToolEdit',
  props: {
    visible: { type: Boolean, default: false },
    scope: { type: String, default: 'self' }, // self | platform（platform 显示 bill_amount）
    urls: { type: Object, required: true },
    toolId: { type: String, default: null }
  },
  data() {
    return {
      form: this.emptyForm(),
      headerRows: [],
      secretRows: [],
      secretOrigKeys: [], // 编辑回填的 secret key 集：提交时被删行补空串（后端 ""=删除）
      serverOptions: [],
      serverOptsLoaded: false,
      mcpToolOptions: [],
      // 库中已保存的 schema；null 表示基准失效（如切换过服务器），此时重新拉取必须保存
      origArgsSchema: null,
      fetchingTools: false,
      submiting: false,
      jsonErr: { body_template: '', args_schema: '' }
    }
  },
  computed: {
    isEdit() { return !!this.toolId },
    rules() {
      const r = {
        name: [
          { required: true, message: '请输入名称', trigger: 'blur' },
          { pattern: /^[a-z0-9][a-z0-9_-]{1,39}$/, message: '仅小写字母/数字/下划线/中划线，2-40 字符', trigger: 'blur' }
        ]
      }
      if (this.form.type === 'http') {
        r.http_url = [{ required: true, message: '请输入请求地址', trigger: 'blur' }]
        if (this.form.http_auth_type !== 'none') {
          r.http_auth_data = [{ required: true, message: '请输入认证数据', trigger: 'blur' }]
        }
      } else {
        r.mcp_server_id = [{ required: true, message: '请选择 MCP 服务', trigger: 'change' }]
        r.mcp_tool_name = [{ required: true, message: '请拉取列表并选择远端工具', trigger: 'change' }]
      }
      return r
    }
  },
  methods: {
    emptyForm() {
      return {
        name: '', description: '', type: 'http', enabled: true,
        http_method: 'POST', http_url: '', http_auth_type: 'none', http_auth_data: '', body_template: '',
        mcp_server_id: '', mcp_tool_name: '',
        args_schema: '', timeout_sec: 30, bill_amount: 0
      }
    },
    reset() {
      this.form = this.emptyForm()
      this.headerRows = []
      this.secretRows = []
      this.secretOrigKeys = []
      this.mcpToolOptions = []
      this.origArgsSchema = null
      this.jsonErr = { body_template: '', args_schema: '' }
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    // server 下拉懒加载：http 工具不需要，首次需要时（切到 mcp / 编辑回填为 mcp）才拉取
    ensureServers() {
      if (this.serverOptsLoaded) return
      this.serverOptsLoaded = true
      this.$tools.asyncPost(this, this.urls.mcpList, agentBody({ page: 1, limit: 0 })).then((res) => {
        if (res.data.code == 0) this.serverOptions = res.data.data.data || []
      }).catch(() => { this.serverOptsLoaded = false }) // 失败允许下次重试
    },
    onTypeChange() {
      if (this.form.type === 'mcp') this.ensureServers()
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    // KV 行：只填 value 未填 key 会被 rowsToMap 静默丢弃，提交前拦下提示
    validateKvRows() {
      if (this.headerRows.some(r => !r.k && r.v)) {
        this.$message({ type: 'warning', message: '「附加请求头」存在未填 Key 的行，请补全 Key 或删除该行' })
        return false
      }
      if (this.secretRows.some(r => !r.k && r.v)) {
        this.$message({ type: 'warning', message: '「密钥 Secrets」存在未填 Key 的行，请补全 Key 或删除该行' })
        return false
      }
      return true
    },
    // 经 mcp-servers/test 握手拉取远端工具列表，防手误键入工具名；resolve 是否成功
    fetchMcpTools() {
      if (!this.form.mcp_server_id) {
        this.$message({ type: 'warning', message: '请先选择 MCP 服务器' })
        return Promise.resolve(false)
      }
      this.fetchingTools = true
      return this.$tools.asyncPost(this, this.urls.mcpTest,
        agentBody({ server_id: this.form.mcp_server_id })).then((res) => {
        let ok = false
        if (res.data.code == 0) {
          this.mcpToolOptions = (res.data.data && res.data.data.tools) || []
          if (!this.mcpToolOptions.length) this.$message({ type: 'warning', message: '该服务器未返回工具列表' })
          // 已选远端工具时同步刷新其参数 schema
          if (this.form.mcp_tool_name) this.applyMcpToolSchema(this.form.mcp_tool_name)
          ok = true
        }
        this.fetchingTools = false
        return ok
      }).catch((err) => {
        this.fetchingTools = false
        if (err && err.isAxiosError) this.$message({ type: 'error', message: '拉取失败，请检查服务器连通性' })
        return false
      })
    },
    // 编辑态一键同步远端工具定义：重新拉取 → 校验工具仍存在 → 与已保存版本不同则直接保存
    refreshAndSaveMcpTool() {
      if (this.fetchingTools || this.submiting) return
      this.fetchMcpTools().then((ok) => {
        if (!ok) return
        if (!this.mcpToolOptions.some((t) => t.name === this.form.mcp_tool_name)) {
          this.$message({ type: 'warning', message: '远端已不存在该工具，请重新选择' })
          return
        }
        // 基准失效（切换过服务器）时不做比较，直接保存新选择
        if (this.origArgsSchema !== null &&
          this.normalizeSchema(this.form.args_schema) === this.normalizeSchema(this.origArgsSchema)) {
          this.$message({ type: 'info', message: '远端工具定义无变化' })
          return
        }
        this.submitForm()
      })
    },
    // schema 语义比较：忽略空白/缩进差异，非法 JSON 退回原字符串比较
    normalizeSchema(text) {
      const s = (text || '').trim()
      if (!s) return ''
      try {
        return JSON.stringify(JSON.parse(s))
      } catch (e) {
        return s
      }
    },
    // 切换 MCP 服务后，旧的工具列表、参数 schema 与已保存基准均不再对应
    onMcpServerChange() {
      this.mcpToolOptions = []
      this.form.mcp_tool_name = ''
      this.form.args_schema = ''
      this.origArgsSchema = null
      this.jsonErr.args_schema = ''
    },
    // 用远端 tools/list 返回的 inputSchema 填充参数 Schema，避免手写与远端不一致
    applyMcpToolSchema(name) {
      this.jsonErr.args_schema = ''
      const t = this.mcpToolOptions.find((x) => x.name === name)
      if (!t) return
      const raw = (t.input_schema || '').trim()
      if (!raw) {
        this.form.args_schema = ''
        return
      }
      try {
        this.form.args_schema = JSON.stringify(JSON.parse(raw), null, 2)
      } catch (e) {
        this.form.args_schema = raw
      }
    },
    // JSON 文本字段提交前校验（失败行内红字，参考 skills 页 parseJsonText 范式）
    parseJsonText(text, field) {
      if (!text || !text.trim()) return null
      try {
        JSON.parse(text)
        this.jsonErr[field] = ''
        return text
      } catch (e) {
        this.jsonErr[field] = '不是合法的 JSON'
        return null
      }
    },
    submitForm() {
      this.$refs.form.validate((valid) => {
        if (!valid) return
        if (!this.validateKvRows()) return
        const bodyTpl = this.parseJsonText(this.form.body_template, 'body_template')
        if (this.form.body_template && bodyTpl === null) return
        const argsSchema = this.parseJsonText(this.form.args_schema, 'args_schema')
        if (this.form.args_schema && argsSchema === null) return
        this.submiting = true
        const payload = {
          name: this.form.name,
          description: this.form.description,
          type: this.form.type,
          enabled: this.form.enabled, // 显式带：后端缺省 true 但不传语义不明
          http_method: this.form.http_method,
          http_url: this.form.http_url,
          http_headers: rowsToMap(this.headerRows),
          http_auth_type: this.form.http_auth_type,
          http_auth_data: this.form.http_auth_data,
          body_template: this.form.body_template,
          mcp_server_id: this.form.mcp_server_id,
          mcp_tool_name: this.form.mcp_tool_name,
          args_schema: this.form.args_schema,
          timeout_sec: this.form.timeout_sec,
          secrets: rowsToMap(this.secretRows, this.secretOrigKeys)
        }
        if (this.scope === 'platform') payload.bill_amount = this.form.bill_amount // 自建版不传，后端强制 0
        if (this.isEdit) payload.tool_id = this.toolId
        const url = this.isEdit ? this.urls.toolsEdit : this.urls.toolsAdd
        this.$tools.asyncPost(this, url, agentBody(payload)).then((res) => {
          if (res.data.code == 0) {
            this.$message({ type: 'success', message: res.data.msg || '保存成功' })
            this.$emit('update:visible', false)
            this.$emit('saved')
          }
          this.submiting = false
        }).catch((err) => {
          this.submiting = false
          if (err && err.isAxiosError) this.$message({ type: 'error', message: '请求失败，请稍后重试' })
        })
      })
    },
    onClose() {
      this.submiting = false
    }
  },
  watch: {
    visible(v) {
      if (!v) return
      this.reset()
      if (this.isEdit) {
        this.$tools.asyncPost(this, this.urls.toolsGet, agentBody({ tool_id: this.toolId })).then((res) => {
          if (res.data.code != 0) return
          const t = res.data.data
          this.form = {
            name: t.name || '', description: t.description || '', type: t.type || 'http',
            enabled: t.enabled !== false,
            http_method: t.http_method || 'POST', http_url: t.http_url || '',
            http_auth_type: t.http_auth_type || 'none', http_auth_data: t.http_auth_data || '',
            body_template: t.body_template || '',
            mcp_server_id: t.mcp_server_id || '', mcp_tool_name: t.mcp_tool_name || '',
            args_schema: t.args_schema || '', timeout_sec: t.timeout_sec || 30,
            bill_amount: t.bill_amount || 0
          }
          this.headerRows = mapToRows(t.http_headers)
          this.secretRows = mapToRows(t.secrets_masked) // 值为 ***，不动即保留原密文
          this.secretOrigKeys = Object.keys(t.secrets_masked || {})
          this.origArgsSchema = t.args_schema || '' // 自动拉取不改变"已保存"基准，供一键更新比较
          if (this.form.type === 'mcp') {
            this.ensureServers() // mcp 工具编辑回填后才需要 server 下拉
            // 打开编辑即同步远端工具列表，顺带用真实 inputSchema 覆盖历史手填值
            this.fetchMcpTools()
          }
          this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
        }).catch(() => {})
      }
    }
  }
}
</script>

<style lang="less" scoped>
.kv_rows { width: 100%; }
.kv_row {
  display: flex;
  gap: 8px;
  margin-bottom: 6px;
  align-items: center;
}
.kv_tip { font-size: 12px; color: #909399; margin-bottom: 6px; }
.mcp_tool_bar { display: flex; gap: 8px; width: 100%; }
.mcp_opt_name { float: left; }
.mcp_opt_desc { float: right; color: #909399; font-size: 12px; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.json_err { color: #f56c6c; font-size: 12px; line-height: 1.4; }
.field_tip { color: #909399; font-size: 12px; margin-left: 8px; }
.mono { font-family: Consolas, Monaco, monospace; }
.danger_text { color: #f56c6c; }
</style>
